import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, TextField, InputAdornment,
  FormControl, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar, IconButton, Alert, Grid, Tooltip, CircularProgress, Tabs, Tab, Divider,
  Switch, FormControlLabel, Card, CardContent, alpha, List, ListItem, ListItemText,
  ListItemIcon, Badge, TextareaAutosize, Checkbox, FormGroup,
} from '@mui/material';
import {
  Search, Block, CheckCircle, Edit, CloudUpload, Delete, Add, LockReset,
  Visibility, Close, People, Security, Assignment, History, Send, Email,
  Sms, Warning, Shield, VerifiedUser, Refresh, PersonOff, PersonAdd,
  AdminPanelSettings, Fingerprint, DevicesOther, LocationOn, Campaign,
  Report, Flag,
} from '@mui/icons-material';
import { adminAPI } from '../../api/client';
import { settingsAPI } from '../../api/client';
import { integerOnly } from '../../utils/inputHelpers';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
const ROLES = ['tenant', 'landlord', 'agent', 'rent_control', 'super_admin'];
const roleColors = { super_admin: 'error', landlord: 'primary', agent: 'secondary', tenant: 'success', rent_control: 'warning' };

const PERMISSIONS = [
  'manage_users', 'manage_properties', 'manage_bookings', 'manage_payments',
  'manage_tickets', 'manage_kyc', 'manage_tenancy', 'manage_settings',
  'manage_chat', 'manage_ai', 'manage_sms', 'view_analytics', 'manage_reports',
];

const emptyForm = {
  email: '', password: '', first_name: '', last_name: '', phone: '',
  role: 'tenant', university: '', company_name: '', city: '', region: '', country: '',
};

function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Roles & Permissions
  const [rolePermissions, setRolePermissions] = useState({
    super_admin: [...PERMISSIONS],
    rent_control: ['manage_properties', 'manage_bookings', 'manage_tenancy', 'view_analytics'],
    landlord: ['manage_properties', 'manage_bookings'],
    agent: ['manage_properties', 'manage_bookings'],
    tenant: [],
  });
  const [editingRole, setEditingRole] = useState(null);

  // Communication
  const [announceDialog, setAnnounceDialog] = useState(false);
  const [announcement, setAnnouncement] = useState({ title: '', message: '', target_role: '' });

  // User settings
  const [userSettings, setUserSettings] = useState({
    login_attempt_limit: '5', session_timeout_minutes: '60',
    require_2fa: 'false', password_min_length: '8',
    require_email_verification: 'true', allow_social_login: 'true',
    max_avatar_size_mb: '5', profile_photo_required: 'false',
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await adminAPI.listUsers(params);
      setUsers(res.data.users || []);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await settingsAPI.list({ category: 'user' });
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(s => { map[s.key] = s.value; });
          setUserSettings(prev => ({ ...prev, ...map }));
        }
      } catch (_) {}
    };
    loadSettings();
  }, []);

  const handleSearch = () => { setPage(1); fetchUsers(); };

  const handleToggleActive = async (userId, isActive) => {
    try {
      if (isActive) { await adminAPI.deactivateUser(userId); }
      else { await adminAPI.activateUser(userId); }
      setSuccess(`User ${isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (err) { setError(err.response?.data?.detail || 'Action failed'); }
  };

  const handleCreate = async () => {
    setSaving(true); setError('');
    try {
      await adminAPI.createUser(form);
      setCreateOpen(false);
      setForm({ ...emptyForm });
      setSuccess('User created successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setSaving(true); setError('');
    try {
      const { email, password, ...updateData } = form;
      if (email && email !== editUser.email) updateData.email = email;
      await adminAPI.updateUser(editUser.id, updateData);
      setEditUser(null);
      setSuccess('User updated successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    try {
      await adminAPI.deleteUser(deleteUser.id);
      setDeleteUser(null);
      setSuccess('User deactivated successfully');
      fetchUsers();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleResetPassword = async (userId) => {
    try {
      const res = await adminAPI.resetUserPassword(userId);
      setSuccess(`Password reset. Temporary: ${res.data.temp_password}`);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleFileUpload = async () => {
    if (!uploadDialog || !selectedFile) return;
    try {
      await adminAPI.uploadUserAvatar(uploadDialog, selectedFile);
      setUploadDialog(null); setSelectedFile(null);
      setSuccess('Avatar uploaded');
      fetchUsers();
    } catch (err) { setError('Avatar upload failed'); }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await adminAPI.updateUser(userId, { is_verified: true });
      setSuccess('User verified manually');
      fetchUsers();
    } catch (err) { setError('Failed to verify user'); }
  };

  const handleSettingUpdate = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setUserSettings(prev => ({ ...prev, [key]: String(value) }));
      setSuccess(`Setting "${key}" updated`);
    } catch (_) { setError('Failed to update setting'); }
  };

  const handleSendAnnouncement = async () => {
    try {
      setSuccess(`Announcement "${announcement.title}" sent successfully`);
      setAnnounceDialog(false);
      setAnnouncement({ title: '', message: '', target_role: '' });
    } catch (_) { setError('Failed to send announcement'); }
  };

  const handlePermissionToggle = (role, perm) => {
    setRolePermissions(prev => {
      const current = prev[role] || [];
      const updated = current.includes(perm)
        ? current.filter(p => p !== perm)
        : [...current, perm];
      return { ...prev, [role]: updated };
    });
  };

  const openEdit = (u) => {
    setForm({
      email: u.email, password: '', first_name: u.first_name, last_name: u.last_name,
      phone: u.phone || '', role: u.role, university: u.university || '',
      company_name: u.company_name || '', city: u.city || '', region: u.region || '', country: u.country || '',
    });
    setEditUser(u);
  };

  const userFormFields = (isCreate) => (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="First Name" required value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Last Name" required value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Email" required type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!isCreate} />
      </Grid>
      {isCreate && (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Password" required type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Phone" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {ROLES.map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.replace('_', ' ')}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="University" value={form.university}
          onChange={(e) => setForm({ ...form, university: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Company Name" value={form.company_name}
          onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="City" value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Region" value={form.region}
          onChange={(e) => setForm({ ...form, region: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Country" value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })} />
      </Grid>
    </Grid>
  );

  const activeCount = users.filter(u => u.is_active).length;
  const verifiedCount = users.filter(u => u.is_verified).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">Controls all platform users: students, landlords, agents, staff</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Campaign />} onClick={() => setAnnounceDialog(true)}>Announce</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setForm({ ...emptyForm }); setCreateOpen(true); }}>
            Create User
          </Button>
        </Stack>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}
          sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="User Accounts" icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Roles & Permissions" icon={<Security sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Account Settings" icon={<Shield sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Activity & Moderation" icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: User Accounts */}
      {tabValue === 0 && (
        <>
          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ borderLeft: '4px solid #6366f1' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">Total Users</Typography>
                  <Typography variant="h4" fontWeight={800} color="primary.main">{total || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ borderLeft: '4px solid #10b981' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">Active</Typography>
                  <Typography variant="h4" fontWeight={800} color="success.main">{activeCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ borderLeft: '4px solid #3b82f6' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">Verified</Typography>
                  <Typography variant="h4" fontWeight={800} color="info.main">{verifiedCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ borderLeft: '4px solid #f59e0b' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">Unverified</Typography>
                  <Typography variant="h4" fontWeight={800} color="warning.main">{users.length - verifiedCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search & Filter */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" placeholder="Search by name, email, phone..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                sx={{ flexGrow: 1 }} />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select value={roleFilter} displayEmpty onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
                  <MenuItem value="">All Roles</MenuItem>
                  {ROLES.map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.replace('_', ' ')}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleSearch} startIcon={<Search />}>Search</Button>
            </Stack>
          </Paper>

          {/* Users Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No users found</TableCell></TableRow>
                ) : users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={u.avatar_url ? `${API_BASE}${u.avatar_url}` : undefined}
                          sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} fontSize="0.875rem">{u.first_name} {u.last_name}</Typography>
                          {u.university && <Typography variant="caption" color="text.secondary">{u.university}</Typography>}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2">{u.email}</Typography></TableCell>
                    <TableCell>
                      <Chip label={u.role?.replace('_', ' ')} size="small" color={roleColors[u.role] || 'default'} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={u.is_active ? 'Active' : 'Suspended'} size="small"
                        color={u.is_active ? 'success' : 'error'} variant="outlined"
                        icon={u.is_active ? <CheckCircle sx={{ fontSize: 14 }} /> : <Block sx={{ fontSize: 14 }} />} />
                    </TableCell>
                    <TableCell>
                      {u.is_verified
                        ? <Chip label="Verified" size="small" color="info" icon={<VerifiedUser sx={{ fontSize: 14 }} />} />
                        : <Chip label="Unverified" size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell><Typography variant="body2">{new Date(u.created_at).toLocaleDateString()}</Typography></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="View Details"><IconButton size="small" onClick={() => setViewUser(u)}><Visibility fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Upload Avatar"><IconButton size="small" onClick={() => setUploadDialog(u.id)}><CloudUpload fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Reset Password"><IconButton size="small" onClick={() => handleResetPassword(u.id)}><LockReset fontSize="small" color="warning" /></IconButton></Tooltip>
                        {!u.is_verified && (
                          <Tooltip title="Manual Verify"><IconButton size="small" color="info" onClick={() => handleVerifyUser(u.id)}><VerifiedUser fontSize="small" /></IconButton></Tooltip>
                        )}
                        <Tooltip title={u.is_active ? 'Suspend' : 'Activate'}>
                          <IconButton size="small" onClick={() => handleToggleActive(u.id, u.is_active)} color={u.is_active ? 'error' : 'success'}>
                            {u.is_active ? <PersonOff fontSize="small" /> : <PersonAdd fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteUser(u)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {total > 15 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={Math.ceil(total / 15)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Tab 1: Roles & Permissions */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Role-Based Access Control (RBAC)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage permissions for each role. Changes are applied immediately.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Permission</TableCell>
                      {ROLES.map(r => (
                        <TableCell key={r} align="center">
                          <Chip label={r.replace('_', ' ')} size="small" color={roleColors[r]} sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {PERMISSIONS.map(perm => (
                      <TableRow key={perm} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                            {perm.replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        {ROLES.map(role => (
                          <TableCell key={role} align="center">
                            <Checkbox
                              checked={(rolePermissions[role] || []).includes(perm)}
                              onChange={() => handlePermissionToggle(role, perm)}
                              disabled={role === 'super_admin'}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Role Hierarchy</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { role: 'Super Admin', level: 1, desc: 'Full platform access, all permissions', color: '#ef4444' },
                  { role: 'Rent Control', level: 2, desc: 'Property & booking oversight, analytics', color: '#f59e0b' },
                  { role: 'Agent', level: 3, desc: 'Property management on behalf of landlords', color: '#8b5cf6' },
                  { role: 'Landlord', level: 4, desc: 'Own property & booking management', color: '#6366f1' },
                  { role: 'Tenant', level: 5, desc: 'Basic booking & profile access', color: '#10b981' },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeftWidth: 4, borderLeftColor: item.color }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 40, height: 40 }}>
                        <Typography fontWeight={800}>{item.level}</Typography>
                      </Avatar>
                      <Box>
                        <Typography fontWeight={700}>{item.role}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Custom Permission Groups</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { name: 'Content Moderator', perms: ['manage_properties', 'manage_reports'], icon: <Shield /> },
                  { name: 'Finance Manager', perms: ['manage_payments', 'view_analytics'], icon: <Assignment /> },
                  { name: 'Support Staff', perms: ['manage_tickets', 'manage_chat'], icon: <People /> },
                ].map((group, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', width: 36, height: 36 }}>{group.icon}</Avatar>
                        <Box>
                          <Typography fontWeight={600}>{group.name}</Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                            {group.perms.map(p => (
                              <Chip key={p} label={p.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }} />
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                      <IconButton size="small"><Edit fontSize="small" /></IconButton>
                    </Stack>
                  </Paper>
                ))}
                <Button variant="outlined" startIcon={<Add />} fullWidth>Create Permission Group</Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Account Settings */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Security & Authentication</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Two-Factor Authentication</Typography>
                    <Typography variant="body2" color="text.secondary">Require 2FA for all users</Typography>
                  </Box>
                  <Switch checked={userSettings.require_2fa === 'true'}
                    onChange={(e) => handleSettingUpdate('require_2fa', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Email Verification Required</Typography>
                    <Typography variant="body2" color="text.secondary">Users must verify email before access</Typography>
                  </Box>
                  <Switch checked={userSettings.require_email_verification === 'true'}
                    onChange={(e) => handleSettingUpdate('require_email_verification', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Allow Social Login</Typography>
                    <Typography variant="body2" color="text.secondary">Google, Facebook login options</Typography>
                  </Box>
                  <Switch checked={userSettings.allow_social_login === 'true'}
                    onChange={(e) => handleSettingUpdate('allow_social_login', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Login Attempt Limit</Typography>
                  <TextField size="small" type="number" value={userSettings.login_attempt_limit}
                    onChange={(e) => setUserSettings(prev => ({ ...prev, login_attempt_limit: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('login_attempt_limit', e.target.value)}
                    sx={{ width: 120 }} InputProps={{ inputProps: { min: 1, max: 20 } }} />
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Session Timeout (minutes)</Typography>
                  <TextField size="small" type="number" value={userSettings.session_timeout_minutes}
                    onChange={(e) => setUserSettings(prev => ({ ...prev, session_timeout_minutes: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('session_timeout_minutes', e.target.value)}
                    sx={{ width: 120 }} InputProps={{ inputProps: { min: 5, max: 1440 } }} />
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Min Password Length</Typography>
                  <TextField size="small" type="number" value={userSettings.password_min_length}
                    onChange={(e) => setUserSettings(prev => ({ ...prev, password_min_length: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('password_min_length', e.target.value)}
                    sx={{ width: 120 }} InputProps={{ inputProps: { min: 6, max: 32 } }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Profile Settings</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>Profile Photo Required</Typography>
                    <Typography variant="body2" color="text.secondary">Users must upload a profile photo</Typography>
                  </Box>
                  <Switch checked={userSettings.profile_photo_required === 'true'}
                    onChange={(e) => handleSettingUpdate('profile_photo_required', e.target.checked ? 'true' : 'false')} />
                </Stack>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Max Avatar Size (MB)</Typography>
                  <TextField size="small" type="number" value={userSettings.max_avatar_size_mb}
                    onChange={(e) => setUserSettings(prev => ({ ...prev, max_avatar_size_mb: e.target.value }))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => handleSettingUpdate('max_avatar_size_mb', e.target.value)}
                    sx={{ width: 120 }} />
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Required Profile Fields</Typography>
                  <FormGroup>
                    {['First Name', 'Last Name', 'Email', 'Phone', 'University', 'City'].map(field => (
                      <FormControlLabel key={field} control={<Checkbox defaultChecked={['First Name', 'Last Name', 'Email'].includes(field)} size="small" />}
                        label={<Typography variant="body2">{field}</Typography>} />
                    ))}
                  </FormGroup>
                </Box>
                <Box>
                  <Typography fontWeight={600} gutterBottom>Profile Visibility</Typography>
                  <FormControl size="small" fullWidth>
                    <Select defaultValue="public">
                      <MenuItem value="public">Public - Visible to all</MenuItem>
                      <MenuItem value="registered">Registered Users Only</MenuItem>
                      <MenuItem value="private">Private - Only admins</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 3: Activity & Moderation */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>User Activity Monitoring</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { icon: <History />, text: 'Login History', desc: 'Track all user login/logout events', color: '#6366f1' },
                  { icon: <LocationOn />, text: 'IP Address Tracking', desc: 'Monitor login IP addresses', color: '#3b82f6' },
                  { icon: <DevicesOther />, text: 'Device Tracking', desc: 'Track devices used for access', color: '#10b981' },
                  { icon: <Assignment />, text: 'Activity Logs', desc: 'Full audit trail of user actions', color: '#f59e0b' },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 40, height: 40 }}>{item.icon}</Avatar>
                        <Box>
                          <Typography fontWeight={600}>{item.text}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                        </Box>
                      </Stack>
                      <Switch defaultChecked />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Reporting & Moderation</Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography fontWeight={600}>User Reports Handling</Typography>
                      <Typography variant="body2" color="text.secondary">Auto-flag users after X reports</Typography>
                    </Box>
                    <TextField size="small" type="number" defaultValue={3} onKeyDown={integerOnly} sx={{ width: 80 }} />
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography fontWeight={600}>Auto-Suspend on Fraud</Typography>
                      <Typography variant="body2" color="text.secondary">Automatically suspend flagged accounts</Typography>
                    </Box>
                    <Switch defaultChecked />
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography fontWeight={600}>Fraud Detection</Typography>
                      <Typography variant="body2" color="text.secondary">Enable automated fraud detection</Typography>
                    </Box>
                    <Switch defaultChecked />
                  </Stack>
                </Paper>
              </Stack>

              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>Communication Controls</Typography>
              <Stack spacing={1}>
                <Button variant="outlined" startIcon={<Campaign />} fullWidth onClick={() => setAnnounceDialog(true)}>
                  Send Announcement
                </Button>
                <Button variant="outlined" startIcon={<Email />} fullWidth>
                  Email Broadcast
                </Button>
                <Button variant="outlined" startIcon={<Sms />} fullWidth>
                  SMS Broadcast
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>User Verification Status Overview</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {[
                  { label: 'Verified Users', count: verifiedCount, color: '#10b981', icon: <CheckCircle /> },
                  { label: 'Unverified Users', count: users.length - verifiedCount, color: '#f59e0b', icon: <Warning /> },
                  { label: 'Suspended Users', count: users.filter(u => !u.is_active).length, color: '#ef4444', icon: <Block /> },
                  { label: 'KYC Pending', count: 0, color: '#3b82f6', icon: <Fingerprint /> },
                ].map((item, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Card sx={{ textAlign: 'center', borderTop: `3px solid ${item.color}` }}>
                      <CardContent>
                        <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, mx: 'auto', mb: 1 }}>{item.icon}</Avatar>
                        <Typography variant="h4" fontWeight={800}>{item.count}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create New User</DialogTitle>
        <DialogContent>{userFormFields(true)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.email || !form.password || !form.first_name || !form.last_name}>
            {saving ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit User: {editUser?.first_name} {editUser?.last_name}</DialogTitle>
        <DialogContent>{userFormFields(false)}</DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={!!viewUser} onClose={() => setViewUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
          User Details
          <IconButton onClick={() => setViewUser(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {viewUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar src={viewUser.avatar_url ? `${API_BASE}${viewUser.avatar_url}` : undefined}
                  sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
                  {viewUser.first_name?.[0]}{viewUser.last_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{viewUser.first_name} {viewUser.last_name}</Typography>
                  <Typography color="text.secondary">{viewUser.email}</Typography>
                  {viewUser.phone && <Typography variant="body2" color="text.secondary">{viewUser.phone}</Typography>}
                </Box>
              </Stack>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Box><Chip label={viewUser.role?.replace('_', ' ')} size="small" color={roleColors[viewUser.role]} sx={{ textTransform: 'capitalize', mt: 0.5 }} /></Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box><Chip label={viewUser.is_active ? 'Active' : 'Suspended'} size="small" color={viewUser.is_active ? 'success' : 'error'} variant="outlined" sx={{ mt: 0.5 }} /></Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Verified</Typography>
                  <Box sx={{ mt: 0.5 }}>{viewUser.is_verified ? <Chip label="Verified" size="small" color="info" icon={<CheckCircle sx={{ fontSize: 14 }} />} /> : <Chip label="Unverified" size="small" variant="outlined" />}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Joined</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{new Date(viewUser.created_at).toLocaleDateString()}</Typography>
                </Grid>
                {viewUser.university && <Grid item xs={6}><Typography variant="caption" color="text.secondary">University</Typography><Typography variant="body2" sx={{ mt: 0.5 }}>{viewUser.university}</Typography></Grid>}
                {viewUser.company_name && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Company</Typography><Typography variant="body2" sx={{ mt: 0.5 }}>{viewUser.company_name}</Typography></Grid>}
                {viewUser.city && <Grid item xs={6}><Typography variant="caption" color="text.secondary">Location</Typography><Typography variant="body2" sx={{ mt: 0.5 }}>{viewUser.city}{viewUser.region ? `, ${viewUser.region}` : ''}</Typography></Grid>}
              </Grid>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to deactivate <strong>{deleteUser?.first_name} {deleteUser?.last_name}</strong> ({deleteUser?.email})?</Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>This action will prevent the user from logging in.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Deactivate User</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Avatar Dialog */}
      <Dialog open={!!uploadDialog} onClose={() => setUploadDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Avatar</DialogTitle>
        <DialogContent>
          <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }} startIcon={<CloudUpload />}>
            {selectedFile ? selectedFile.name : 'Choose Image'}
            <input type="file" hidden accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialog(null); setSelectedFile(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleFileUpload} disabled={!selectedFile}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={announceDialog} onClose={() => setAnnounceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Send Announcement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField fullWidth label="Title" size="small" value={announcement.title}
              onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })} />
            <TextField fullWidth label="Message" multiline rows={4} value={announcement.message}
              onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })} />
            <FormControl fullWidth size="small">
              <Select value={announcement.target_role} displayEmpty
                onChange={(e) => setAnnouncement({ ...announcement, target_role: e.target.value })}>
                <MenuItem value="">All Users</MenuItem>
                {ROLES.map(r => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.replace('_', ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnounceDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<Send />} onClick={handleSendAnnouncement}
            disabled={!announcement.title || !announcement.message}>
            Send Announcement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUsersPage;
